import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

function formatSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isSpreadsheet(artifact) {
  const kind = String(artifact?.kind || "").toLowerCase();
  const mime = String(artifact?.mime || "").toLowerCase();
  const name = String(artifact?.fileName || "").toLowerCase();
  return (
    kind === "xlsx" ||
    kind === "csv" ||
    mime.includes("spreadsheet") ||
    mime === "text/csv" ||
    name.endsWith(".xlsx") ||
    name.endsWith(".csv")
  );
}

/**
 * Card do arquivo gerado pelo assistente.
 *
 * O link vem assinado e com prazo, então o card abre em nova aba em vez de
 * guardar a URL: link expirado é melhor que link eterno circulando por aí.
 */
export default function AssistantArtifactCard({ artifact }) {
  const { t } = useTranslation("shell");
  if (!artifact?.url) return null;

  const size = formatSize(artifact.sizeBytes);
  const spreadsheet = isSpreadsheet(artifact);
  const pages = Number(artifact.pageCount) || 0;
  const rows = Number(artifact.rowCount) || 0;

  const detalhes = [
    spreadsheet && rows
      ? t("platformAi.artifactRows", { defaultValue: "{{count}} linhas", count: rows })
      : null,
    spreadsheet && pages > 1
      ? t("platformAi.artifactSheets", { defaultValue: "{{count}} abas", count: pages })
      : null,
    !spreadsheet && pages
      ? t("platformAi.artifactPages", { defaultValue: "{{count}} páginas", count: pages })
      : null,
    size,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mt: 0.75,
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      {spreadsheet ? <TableChartIcon color="success" /> : <PictureAsPdfIcon color="error" />}
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={artifact.title}>
          {artifact.title || artifact.fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detalhes ||
            t("platformAi.artifactReady", { defaultValue: "Arquivo pronto para download" })}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon />}
        href={artifact.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("platformAi.artifactDownload", { defaultValue: "Baixar" })}
      </Button>
    </Box>
  );
}
